// =====================================================
// 白板协作 - React Hook
// =====================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { CaptureUpdateAction, reconcileElements } from '@excalidraw/excalidraw';
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import type { OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import { supabase } from '@/config/supabase';
import { CollabManager } from './CollabManager';
import type {
  CollabConnectionStatus,
  CollabUser,
  CollaborationEndedReason,
  WhiteboardProject,
} from '../types';
import * as service from '../services/whiteboard-service';

export interface UseCollaborationReturn {
  /** 会话已开启（包括短暂断线后的自动恢复期） */
  isCollaborating: boolean;
  isOwner: boolean;
  collaborators: Map<string, CollabUser>;
  connectionStatus: CollabConnectionStatus;
  connectionError: string | null;
  startCollab: () => Promise<string | null>;
  stopCollab: () => Promise<void>;
  /** 仅退出本地协作会话，不修改分享配置。 */
  leaveCollab: () => Promise<void>;
  removeCollaborator: (userId: string) => Promise<void>;
  validateAccess: () => Promise<boolean>;
  onPointerUpdate: ((payload: any) => void) | undefined;
  syncElements: (elements: readonly OrderedExcalidrawElement[]) => void;
  /** 在 manager 写入远端场景的同步回调中跳过重复广播。 */
  isApplyingRemoteScene: () => boolean;
  shareInfo: { token: string; link: string } | null;
  endedReason: CollaborationEndedReason | null;
  clearEndedReason: () => void;
}

type CurrentUser = {
  userId: string;
  username: string;
  email: string;
  avatarUrl?: string;
};

export function useCollaboration(
  projectId: string | undefined,
  excalidrawAPI: ExcalidrawImperativeAPI | null,
  shareToken: string | null,
  currentProject: WhiteboardProject | null,
): UseCollaborationReturn {
  const managerRef = useRef<CollabManager | null>(null);
  const joiningRef = useRef(false);
  const closedLocallyRef = useRef(false);
  const [isCollaborating, setIsCollaborating] = useState(false);
  const [collaborators, setCollaborators] = useState<Map<string, CollabUser>>(new Map());
  const [shareInfo, setShareInfo] = useState<{ token: string; link: string } | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<CollabConnectionStatus>('disconnected');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [endedReason, setEndedReason] = useState<CollaborationEndedReason | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      const profile = await service.fetchUserProfile(user.id);
      if (!cancelled) {
        setCurrentUser({
          userId: user.id,
          username: profile.name,
          email: profile.email || user.email || '',
          avatarUrl: profile.avatarUrl,
        });
      }
    };
    void loadUser();
    return () => { cancelled = true; };
  }, []);

  const resetLocalState = useCallback(() => {
    joiningRef.current = false;
    setIsCollaborating(false);
    setCollaborators(new Map());
    setConnectionStatus('disconnected');
    setConnectionError(null);
    setShareInfo(null);
  }, []);

  const leaveCollab = useCallback(async () => {
    const manager = managerRef.current;
    managerRef.current = null;
    if (manager) await manager.stop();
    resetLocalState();
  }, [resetLocalState]);

  const handleAccessEnded = useCallback((reason: CollaborationEndedReason) => {
    managerRef.current = null;
    resetLocalState();
    setEndedReason(reason);
  }, [resetLocalState]);

  const joinCollab = useCallback(async (pid: string, user: CurrentUser) => {
    if (!excalidrawAPI || managerRef.current || joiningRef.current) return;

    joiningRef.current = true;
    closedLocallyRef.current = false;
    setConnectionStatus('connecting');
    setConnectionError(null);

    const storageKey = currentProject?.storage_key;
    const manager = new CollabManager({
      projectId: pid,
      excalidrawAPI,
      currentUser: user,
      ownerId: currentProject?.created_by,
      onCollaboratorsChange: (nextCollaborators) => {
        setCollaborators(nextCollaborators);
      },
      onConnectionChange: (status, error) => {
        setConnectionStatus(status);
        setConnectionError(error || null);
      },
      onConnectionRecovered: async (isReconnect) => {
        if (!isReconnect) return;
        const access = await service.getWhiteboardAccessState(pid);
        if (!access.hasAccess) {
          handleAccessEnded(access.reason === 'closed' ? 'closed' : 'removed');
          return;
        }
        const latestProject = await service.fetchProjectById(pid, shareToken);
        if (!latestProject) return;

        const latestCanvas = service.loadElements(latestProject);
        const mergedElements = reconcileElements(
          excalidrawAPI.getSceneElementsIncludingDeleted(),
          latestCanvas.elements as any,
          excalidrawAPI.getAppState(),
        );
        if (latestProject.storage_key && latestCanvas.elements.length > 0) {
          const files = await service.loadFiles(latestProject.storage_key, latestCanvas.elements);
          if (Object.keys(files).length > 0) excalidrawAPI.addFiles(Object.values(files));
        }
        excalidrawAPI.updateScene({
          elements: mergedElements,
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      },
      onCollaborationEnded: handleAccessEnded,
      onFilesNeeded: async (imageElements) => (
        storageKey ? service.loadFiles(storageKey, imageElements) : {}
      ),
    });

    // 先占位，防止 React effect 与“开启协作”点击并发创建两个频道。
    managerRef.current = manager;
    try {
      await manager.start();
      setIsCollaborating(true);
      console.log('[Collab] 已加入协作频道:', pid);
    } catch (error) {
      managerRef.current = null;
      await manager.stop();
      const message = error instanceof Error ? error.message : '无法连接协作频道';
      setConnectionStatus('error');
      setConnectionError(message);
    } finally {
      joiningRef.current = false;
    }
  }, [currentProject?.created_by, currentProject?.storage_key, excalidrawAPI, handleAccessEnded, shareToken]);

  useEffect(() => {
    if (!projectId || !excalidrawAPI || !currentUser || !currentProject) return;
    if (closedLocallyRef.current || managerRef.current || joiningRef.current) return;
    if (currentProject.share_mode !== 'edit' || !currentProject.share_token) return;

    // currentProject 已由 token RPC 或成员 RPC 鉴权；后续从“与我协作”进入无需再次携带 token。

    const link = `${window.location.origin}/whiteboard/${projectId}?token=${currentProject.share_token}`;
    setShareInfo({ token: currentProject.share_token, link });
    void joinCollab(projectId, currentUser);
  }, [currentProject, currentUser, excalidrawAPI, joinCollab, projectId, shareToken]);

  const startCollab = useCallback(async (): Promise<string | null> => {
    if (!projectId || !currentUser) return null;

    try {
      const token = await service.enableCollaboration(projectId);
      const link = `${window.location.origin}/whiteboard/${projectId}?token=${token}`;
      closedLocallyRef.current = false;
      setShareInfo({ token, link });
      await joinCollab(projectId, currentUser);
      return link;
    } catch (error) {
      const message = error instanceof Error ? error.message : '开启协作失败';
      console.error('[Collab] 开启协作失败:', error);
      setConnectionError(message);
      return null;
    }
  }, [currentUser, joinCollab, projectId]);

  const isOwner = Boolean(
    currentUser && currentProject && currentProject.created_by === currentUser.userId,
  );

  const stopCollab = useCallback(async () => {
    if (!projectId || !isOwner) return;

    try {
      const manager = managerRef.current;
      // 先确保远端收到关闭通知，再让数据库令牌失效；否则本端可能先被数据库
      // 更新事件移出频道，导致 collab-ended 永远发不出去。
      await manager?.notifyCollaborationEnded();
      await service.disableCollaboration(projectId);
      managerRef.current = null;
      if (manager) await manager.stop();
      closedLocallyRef.current = true;
      resetLocalState();
    } catch (error) {
      console.error('[Collab] 关闭协作失败:', error);
      setConnectionError(error instanceof Error ? error.message : '关闭协作失败');
    }
  }, [isOwner, projectId, resetLocalState]);

  const removeCollaborator = useCallback(async (userId: string) => {
    if (!projectId || !isOwner) return;
    // 先广播再删除成员，确保目标仍有 Realtime 发送/接收权限。
    await managerRef.current?.notifyCollaboratorRemoved(userId);
    await service.removeWhiteboardCollaborator(projectId, userId);
  }, [isOwner, projectId]);

  const validateAccess = useCallback(async (): Promise<boolean> => {
    if (!projectId || isOwner) return true;
    try {
      const access = await service.getWhiteboardAccessState(projectId);
      if (access.hasAccess) return true;
      await leaveCollab();
      setEndedReason(access.reason === 'closed' ? 'closed' : 'removed');
      return false;
    } catch (error) {
      console.warn('[Collab] 权限复查失败:', error);
      return true;
    }
  }, [isOwner, leaveCollab, projectId]);

  // 浏览器从后台恢复时补查权限，覆盖离线期间遗漏的关闭/移除广播。
  useEffect(() => {
    if (!projectId || !isCollaborating || isOwner) return;
    const onVisibility = () => { if (document.visibilityState === 'visible') void validateAccess(); };
    window.addEventListener('focus', validateAccess);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', validateAccess);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isCollaborating, isOwner, projectId, validateAccess]);

  const syncElements = useCallback((elements: readonly OrderedExcalidrawElement[]) => {
    managerRef.current?.syncElements(elements);
  }, []);

  const isApplyingRemoteScene = useCallback(() => (
    managerRef.current?.isApplyingRemoteScene() || false
  ), []);

  const onPointerUpdate = isCollaborating
    ? managerRef.current?.onPointerUpdate
    : undefined;

  useEffect(() => {
    closedLocallyRef.current = false;
    return () => {
      void leaveCollab();
    };
  }, [leaveCollab, projectId]);

  return {
    isCollaborating,
    isOwner,
    collaborators,
    connectionStatus,
    connectionError,
    startCollab,
    stopCollab,
    leaveCollab,
    removeCollaborator,
    validateAccess,
    onPointerUpdate,
    syncElements,
    isApplyingRemoteScene,
    shareInfo,
    endedReason,
    clearEndedReason: () => setEndedReason(null),
  };
}
